import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux"
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { getDoctorsAction } from "../../../store/actions/DoctorsAction"
import Reshedule from "../../../../src/assets/images/deadline.png";
import backtoqueue from "../../../../src/assets/images/add_back_queue.png";
import Select from "react-select";
import enqueue from "../../../../src/assets/images/enque.png"
import cancel from "../../../../src/assets/images/cancel_app.png"
import { 
  createReceptionistAction, 
  getReceptionistsAction 
} from "../../../store/actions/ReceptionistAction";
import { getBookedAppointmentsAction } from "../../../store/actions/appoinmentAction.js";
import { rescheduleAppointmentAction } from "../../../store/actions/AssignedAppointmentAction";
import { Modal } from "react-bootstrap";
import { getAvailableSlotsAction } from "../../../store/actions/AssignedAppointmentAction";
import { getBranchesAction } from "../../../store/actions/BranchAction";
import { formatName } from "../../../utils/formatName.js"
import defaultAvatar from "../../../../src/assets/images/placeholder.jpg";

// Utility function to get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Convert time to 24-hour format
const convertTo24Hour = (time12h) => {
  if (!time12h) return "00:00";
  const match = time12h.match(/(\d{1,2}):(\d{2})(AM|PM)/i);
  if (!match) return "00:00";
  let [, hours, minutes, meridian] = match;
  hours = parseInt(hours, 10);
  if (meridian.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (meridian.toUpperCase() === "AM" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

// Create local date from date string and time string
const createLocalDate = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) {
    return new Date(NaN);
  }
  const converted = convertTo24Hour(timeStr);
  const [hours, minutes] = converted.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return new Date(NaN);

  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return new Date(NaN);

  return new Date(year, month - 1, day, hours, minutes, 0);
};

// Check if reschedule is allowed (more than 48 hours before)
const isRescheduleAllowed = (appointmentDate, appointmentTime) => {
  if (!appointmentDate || !appointmentTime) return false;
  
  const appointmentDateTime = createLocalDate(appointmentDate, appointmentTime);
  const currentTime = new Date();
  const diffHours = (appointmentDateTime - currentTime) / (1000 * 60 * 60);
  
  return !isNaN(diffHours) && diffHours > 48;
};

const Appointmentlist = () => {
  const dispatch = useDispatch()
  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [leftAppointments, setLeftAppointments] = useState([]);
  const [middleAppointments, setMiddleAppointments] = useState([]);
  const [rightAppointments, setRightAppointments] = useState([]);
  const [enqueuedPatientIds, setEnqueuedPatientIds] = useState([])
  
  // Individual loading states for each action
  const [rescheduleLoading, setRescheduleLoading] = useState({});
  const [cancelLoading, setCancelLoading] = useState({});
  const [enqueueLoading, setEnqueueLoading] = useState({});
  const [rescheduleModalLoading, setRescheduleModalLoading] = useState(false);
  
  // Doctor states
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const doctors = useSelector((state) => state.doctors.doctors || []);
  const loading = useSelector((state) => state.doctors.loading || false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const [modalDate, setModalDate] = useState(getTodayDateString());
  const [modalMode, setModalMode] = useState("Online");
  const [modalBranch, setModalBranch] = useState(null);
  const [modalTimeSlot, setModalTimeSlot] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);

  const { bookedAppointments, loading: appointmentLoading } = useSelector(
    (state) => state.appointment || { bookedAppointments: [], loading: false }
  );
  
  const { availableSlots } = useSelector(
    (state) => state.assignedAppointments || {}
  );

  const branches = useSelector((state) => state.branches?.branches || []);

  // Fetch appointments when date or doctor changes
  useEffect(() => {
    if (selectedDate) {
      dispatch(
        getBookedAppointmentsAction(
          selectedDate,
          selectedDoctorId || null
        )
      );
    }
  }, [selectedDate, selectedDoctorId, dispatch]);

  // Fetch branches on mount
  useEffect(() => {
    dispatch(getBranchesAction());
  }, [dispatch]);

  // Fetch available slots when doctor or modal date changes
  useEffect(() => {
    if (!selectedDoctorId || !modalDate) return;
    dispatch(
      getAvailableSlotsAction({
        doctor_id: selectedDoctorId,
        date: modalDate,
      })
    );
  }, [selectedDoctorId, modalDate, dispatch]);

  // Update time slots when availableSlots changes
  useEffect(() => {
    if (!availableSlots || availableSlots.length === 0) {
      setTimeSlots([]);
      setModalTimeSlot(null);
      return;
    }

    const mapped = availableSlots.map((slot) => ({
      value: slot.time_slot,
      label: slot.time_slot,
      opening_hour_id: slot.opening_hour_id,
      available_slots: slot.available_slots,
      max_appointments: slot.max_appointments,
    }));

    setTimeSlots(mapped);
  }, [availableSlots]);

  // Split appointments into three columns
  useEffect(() => {
    if (Array.isArray(bookedAppointments)) {
      setLeftAppointments(bookedAppointments.slice(0, 5));
      setMiddleAppointments(bookedAppointments.slice(5, 10));
      setRightAppointments(bookedAppointments.slice(10, 15));
    }
  }, [bookedAppointments]);

  // Fetch doctors on mount
  useEffect(() => {
    dispatch(getDoctorsAction(false));
  }, [dispatch]);

  // Create doctor options from doctors data
  const doctorOptions = doctors.map((doctor) => ({
    value: doctor.doctor_id,
    label: doctor.doctor_name,
    specialization: doctor.specialization,
  }));

  const selectedDoctorOption = doctorOptions.find((option) =>
    String(option.value) === String(selectedDoctorId)
  );

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  );

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: '50px',
      fontSize: '14px',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
    }),
    input: (base) => ({
      ...base,
      margin: '0px',
      fontSize: '14px',
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '14px',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: '14px',
    }),
  };

  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctorId(selectedOption ? selectedOption.value : "");
  };

  // Handle reschedule with 48-hour restriction
  const handleReschedule = async (appointment) => {
    const appointmentId = appointment.id || appointment.appointment_id;
    
    if (rescheduleLoading[appointmentId]) return;
    
    setRescheduleLoading(prev => ({ ...prev, [appointmentId]: true }));
    
    try {
      const timeSlot = appointment.timeSlot || appointment.time_slot;
      const [timePart] = timeSlot?.split(" to ") || [];
      
      if (!timePart) {
        await Swal.fire({
          icon: "error",
          title: "Cannot Reschedule",
          text: "Appointment time is missing or invalid.",
        });
        return;
      }
      
      const rescheduleCount = parseInt(appointment.rescheduled_cnt) || 0;
      if (rescheduleCount >= 1) {
        await Swal.fire({
          icon: "warning",
          title: "Reschedule Not Allowed",
          text: "This appointment has already been rescheduled once. Only one reschedule is permitted per appointment.",
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });
        return;
      }
      
      const isAllowed = isRescheduleAllowed(appointment.date, timePart);
      
      if (!isAllowed) {
        const appointmentDateTime = createLocalDate(appointment.date, timePart);
        const currentTime = new Date();
        const diffHours = (appointmentDateTime - currentTime) / (1000 * 60 * 60);
        const hoursRemaining = Math.floor(diffHours);
        const daysRemaining = Math.floor(hoursRemaining / 24);
        const remainingHours = hoursRemaining % 24;
        
        await Swal.fire({
          icon: "warning",
          title: "Cannot Reschedule",
          html: `Appointments can only be rescheduled <strong>more than 48 hours</strong> before the appointment time.<br/><br/>
          <strong>Appointment Details:</strong><br/>
          📅 Date: ${appointment.date}<br/>
          ⏰ Time: ${timePart}<br/>
          ⏱️ Hours remaining: ${hoursRemaining} hours<br/>
          ${daysRemaining > 0 ? `📆 ${daysRemaining} days and ${remainingHours} hours remaining` : ''}<br/><br/>`,
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });
        return;
      }
      
      setSelectedAppointment(appointment);
      setModalDate(appointment.date || selectedDate);
      setModalMode(appointment.mode || "Online");
      
      const branchId = appointment.branch_id || appointment.branchId;
      const branchName = appointment.branch || appointment.branchName;
      
      if (branchId) {
        const branchOption = branches.find(b => 
          String(b.branch_id) === String(branchId)
        );
        if (branchOption) {
          setModalBranch({
            value: branchOption.branch_id,
            label: branchOption.branch_name,
          });
        } else {
          setModalBranch(null);
        }
      } else if (branchName) {
        const branchOption = branches.find(b => 
          String(b.branch_name) === String(branchName)
        );
        if (branchOption) {
          setModalBranch({
            value: branchOption.branch_id,
            label: branchOption.branch_name,
          });
        } else {
          setModalBranch(null);
        }
      } else {
        setModalBranch(null);
      }

      setShowRescheduleModal(true);
    } finally {
      setRescheduleLoading(prev => ({ ...prev, [appointmentId]: false }));
    }
  };

  const handleRescheduleConfirm = async () => {
    if (!modalDate) {
      Swal.fire("Error", "Please select date", "error");
      return;
    }

    if (!modalBranch) {
      Swal.fire("Error", "Please select branch", "error");
      return;
    }

    const today = getTodayDateString();
    if (modalDate < today) {
      Swal.fire("Error", "Cannot reschedule to a past date", "error");
      return;
    }

    const payload = {
      appointment_id: Number(selectedAppointment?.id || selectedAppointment?.appointment_id),
      date: modalDate,
      mode: modalMode,
      branch: modalBranch?.value || "",
    };
    
    setRescheduleModalLoading(true);
    
    try {
      const res = await dispatch(rescheduleAppointmentAction(payload));

      if (res?.status === "success") {
        Swal.fire("Success", res.message, "success");
        setShowRescheduleModal(false);
        
        setModalTimeSlot(null);
        setModalBranch(null);
        
        dispatch(
          getBookedAppointmentsAction(
            selectedDate,
            selectedDoctorId || null
          )
        );
      } else {
        throw new Error(res?.message || "Failed to reschedule");
      }

    } catch (err) {
      console.error("COMPONENT ERROR:", err);
      Swal.fire("Error", err.message || "Failed to reschedule", "error");
    } finally {
      setRescheduleModalLoading(false);
    }
  };

  const handleEnqueue = async (appointment) => {
    const patientId = appointment.patient_id;
    
    if (enqueueLoading[patientId]) return;

    setEnqueueLoading(prev => ({ ...prev, [patientId]: true }));

    try {
      const qrcodescanData = JSON.stringify({
        id: String(patientId),
        name: formatName(appointment.name),
        mobile: appointment.phone || appointment.whatsapp_contact_no || "",
      });

      await dispatch(createReceptionistAction(qrcodescanData, appointment.doctor_id));
      
      dispatch(
        getBookedAppointmentsAction(
          selectedDate,
          selectedDoctorId || null
        )
      );

      setEnqueuedPatientIds((prev) => [...prev, patientId]);

      Swal.fire("Success", "Patient enqueued successfully!", "success");
    } catch (err) {
      console.error("Error enqueueing:", err);
      Swal.fire("Error", "Failed to enqueue patient", "error");
    } finally {
      setEnqueueLoading(prev => ({ ...prev, [patientId]: false }));
    }
  };

  const handleRemove = async (appointment) => {
    const appointmentId = appointment.id || appointment.appointment_id;
    
    if (cancelLoading[appointmentId]) return;
    
    const result = await Swal.fire({
      title: "Remove Appointment",
      text: `Are you sure you want to remove ${appointment.name}'s appointment?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setCancelLoading(prev => ({ ...prev, [appointmentId]: true }));
      
      try {
        Swal.fire("Removed", `${appointment.name}'s appointment has been removed.`, "success");
        
        dispatch(
          getBookedAppointmentsAction(
            selectedDate,
            selectedDoctorId || null
          )
        );
      } catch (error) {
        Swal.fire("Error", "Failed to remove appointment", "error");
      } finally {
        setCancelLoading(prev => ({ ...prev, [appointmentId]: false }));
      }
    }
  };

  // Function to get patient avatar
  const getPatientAvatar = (patient) => {
    if (patient.patient_photo && patient.patient_photo !== "") {
      return patient.patient_photo;
    }
    return defaultAvatar;
  };

  const AppointmentCard = ({ patients, title, column }) => (
    <div className="card border-0 h-100" style={{ borderRadius: "0 0 10px 10px" }}>
      <div className="card-body p-0">
        <div className="p-3">
          <div
            className="dz-scroll"
            style={{
              height: "calc(100vh - 300px)",
              overflowY: "auto",
              overflowX: "hidden"
            }}
          >
            <div className="list-group list-group-flush">
              {patients.map((patient, index) => {
                const appointmentId = patient.id || patient.appointment_id;
                const patientId = patient.patient_id;
                const patientAvatar = getPatientAvatar(patient);
                
                // ✅ Check if patient is already enqueued/presented
                const isPresented = patient.queueDetails?.is_presented === "Yes" || 
                                    patient.is_presented === "Yes" ||
                                    enqueuedPatientIds.includes(patientId);
                
                const timeSlot = patient.timeSlot || patient.time_slot;
                const [timePart] = timeSlot?.split(" to ") || [];
                
                const alreadyRescheduled = parseInt(patient.rescheduled_cnt) >= 1;
                const isWithin48Hours = timePart && !isRescheduleAllowed(patient.date, timePart);
                const isReschedulable = !alreadyRescheduled && !isWithin48Hours && timePart && !isPresented;
                
                const isRescheduleLoading = rescheduleLoading[appointmentId];
                const isCancelLoading = cancelLoading[appointmentId];
                const isEnqueueLoading = enqueueLoading[patientId];
                
                // Function to show why reschedule is disabled
                const showRescheduleDisabledMessage = () => {
                  if (isPresented) {
                    Swal.fire({
                      icon: "info",
                      title: "Cannot Reschedule",
                      text: "Patient has already been enqueued. Cannot reschedule.",
                      confirmButtonText: "OK",
                      confirmButtonColor: "#3085d6",
                    });
                  } else if (alreadyRescheduled) {
                    Swal.fire({
                      icon: "info",
                      title: "Reschedule Not Allowed",
                      text: "This appointment has already been rescheduled once. Only one reschedule is permitted per appointment.",
                      confirmButtonText: "OK",
                      confirmButtonColor: "#3085d6",
                    });
                  } else if (isWithin48Hours) {
                    const appointmentDateTime = createLocalDate(patient.date, timePart);
                    const currentTime = new Date();
                    const diffHours = (appointmentDateTime - currentTime) / (1000 * 60 * 60);
                    const hoursRemaining = Math.floor(diffHours);
                    const daysRemaining = Math.floor(hoursRemaining / 24);
                    const remainingHours = hoursRemaining % 24;
                    
                    Swal.fire({
                      icon: "warning",
                      title: "Cannot Reschedule",
                      html: `Appointments can only be rescheduled <strong>more than 48 hours</strong> before the appointment time.<br/><br/>
                      <strong>Appointment Details:</strong><br/>
                      📅 Date: ${patient.date}<br/>
                      ⏰ Time: ${timePart}<br/>
                      ⏱️ Hours remaining: ${hoursRemaining} hours<br/>
                      ${daysRemaining > 0 ? `📆 ${daysRemaining} days and ${remainingHours} hours remaining` : ''}`,
                      confirmButtonText: "OK",
                      confirmButtonColor: "#3085d6",
                    });
                  }
                };
                
                // Function to show why cancel is disabled
                const showCancelDisabledMessage = () => {
                  if (isPresented) {
                    Swal.fire({
                      icon: "info",
                      title: "Cannot Cancel",
                      text: "Patient has already been enqueued. Cannot cancel appointment.",
                      confirmButtonText: "OK",
                      confirmButtonColor: "#3085d6",
                    });
                  }
                };
                
                return (
                  <div
                    key={appointmentId || `patient-${index}`}
                    className="list-group-item border-0 px-0 py-3"
                    style={{ borderBottom: "1px solid #eee !important" }}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center flex-grow-1">
                        <div className="me-3">
                          <img
                            src={patientAvatar}
                            alt={patient.name}
                            width="48"
                            height="48"
                            className="rounded-circle"
                            style={{ objectFit: "cover" }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = defaultAvatar;
                            }}
                          />
                        </div>

                        <div className="flex-grow-1">
                          <h6 className="mb-1" style={{ fontSize: '15px', fontWeight: 600 }}>
                            {patient.name}
                            {patient.diseases && (
                              <span className="text-muted" style={{ fontWeight: 400 }}>
                                {" "}– {patient.diseases}
                              </span>
                            )}
                          </h6>

                          <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                            {patient.doctor}
                            {patient.timeSlot && (
                              <> | {patient.timeSlot}</>
                            )}
                            <span className="ms-1"> | {patient.mode}</span>
                            {patient.branch && (
                              <span className="ms-1"> | {patient.branch}</span>
                            )}
                          </p>
                          
                          {/* Show reschedule count if greater than 0 */}
                          {parseInt(patient.rescheduled_cnt) > 0 && (
                            <span 
                              className="badge bg-info text-white mt-1"
                              style={{ fontSize: "10px" }}
                            >
                              Rescheduled: {patient.rescheduled_cnt}
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        className="d-flex align-items-center"
                        style={{
                          gap: "14px",
                          padding: "4px 0",
                        }}
                      >
                        {/* Cancel Button - Disabled if patient is presented */}
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          {isCancelLoading ? (
                            <div style={{
                              width: "28px",
                              height: "28px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                              <div className="spinner-border spinner-border-sm text-danger" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </div>
                          ) : isPresented ? (
                            <img
                              src={cancel}
                              alt="Cancel Disabled"
                              width={28}
                              height={28}
                              onClick={showCancelDisabledMessage}
                              style={{
                                cursor: "pointer",
                                objectFit: "contain",
                                display: "block",
                                opacity: 0.5,
                              }}
                              title="Patient already enqueued - Cannot cancel"
                            />
                          ) : (
                            <img
                              src={cancel}
                              alt="Cancel Appointment"
                              width={28}
                              height={28}
                              title="Cancel Appointment"
                              onClick={() => handleRemove(patient)}
                              style={{
                                cursor: "pointer",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          )}
                        </div>

                        {/* Reschedule Button - Disabled if patient is presented or other conditions */}
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          {isRescheduleLoading ? (
                            <div style={{
                              width: "28px",
                              height: "28px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </div>
                          ) : isPresented ? (
                            <img
                              src={Reshedule}
                              alt="Reschedule Disabled"
                              width={28}
                              height={28}
                              onClick={showRescheduleDisabledMessage}
                              style={{
                                cursor: "pointer",
                                objectFit: "contain",
                                display: "block",
                                opacity: 0.5,
                              }}
                              title="Patient already enqueued - Cannot reschedule"
                            />
                          ) : isReschedulable ? (
                            <img
                              src={Reshedule}
                              alt="Reschedule"
                              width={28}
                              height={28}
                              title="Reschedule Patient"
                              onClick={() => handleReschedule(patient)}
                              style={{
                                cursor: "pointer",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          ) : (
                            <img
                              src={Reshedule}
                              alt="Reschedule Disabled"
                              width={28}
                              height={28}
                              onClick={showRescheduleDisabledMessage}
                              style={{
                                cursor: "pointer",
                                objectFit: "contain",
                                display: "block",
                                opacity: 0.5,
                              }}
                              title="Cannot reschedule - Check conditions"
                            />
                          )}
                        </div>

                        {/* Enqueue Button - Only show if not already enqueued */}
                        {!isPresented && (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            {isEnqueueLoading ? (
                              <div style={{
                                width: "28px",
                                height: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}>
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={enqueue}
                                alt="Enqueue"
                                width={28}
                                height={28}
                                title="Enqueue Patient"
                                onClick={() => handleEnqueue(patient)}
                                style={{
                                  cursor: "pointer",
                                  objectFit: "contain",
                                  display: "block",
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {patients.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">No appointments found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Single Filter Section */}
      <div className="card border-0 shadow-sm mb-0" style={{ borderRadius: "10px 10px 0 0" }}>
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <h4 className="mb-0" style={{ fontSize: "1.4rem", fontWeight: 600 }}>
              Booked Appointments
            </h4>

            <div className="d-flex gap-3 align-items-center flex-wrap">
              <div style={{ minWidth: "250px" }}>
                <Select
                  value={selectedDoctorOption || null}
                  onChange={handleDoctorChange}
                  options={doctorOptions}
                  formatOptionLabel={formatOptionLabel}
                  placeholder="Select a doctor..."
                  isClearable
                  classNamePrefix="react-select"
                  isDisabled={loading || appointmentLoading}
                  styles={customStyles}
                />
              </div>

              <div style={{ minWidth: "180px", position: "relative" }}>
                <input
                  type="date"
                  className="form-control"
                  value={selectedDate}
                  min={getTodayDateString()}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  disabled={appointmentLoading}
                  style={{ paddingRight: "40px", height: "50px" }}
                />
                <i
                  className="fa fa-calendar"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    fontSize: "18px",
                    color: "#555",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {appointmentLoading && (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!appointmentLoading && (
        <>
          {(leftAppointments.length > 0 ||
            middleAppointments.length > 0 ||
            rightAppointments.length > 0) ? (
            <div className="row g-3">
              {leftAppointments.length > 0 && (
                <div
                  className={
                    middleAppointments.length === 0 && rightAppointments.length === 0
                      ? "col-12"
                      : middleAppointments.length === 0 || rightAppointments.length === 0
                        ? "col-12 col-md-6"
                        : "col-12 col-md-6 col-xl-4"
                  }
                >
                  <AppointmentCard
                    patients={leftAppointments}
                    title="Appointments 1-5"
                    column="left"
                  />
                </div>
              )}

              {middleAppointments.length > 0 && (
                <div
                  className={
                    rightAppointments.length === 0
                      ? "col-12 col-md-6"
                      : "col-12 col-md-6 col-xl-4"
                  }
                >
                  <AppointmentCard
                    patients={middleAppointments}
                    title="Appointments 6-10"
                    column="middle"
                  />
                </div>
              )}

              {rightAppointments.length > 0 && (
                <div className="col-12 col-md-6 col-xl-4">
                  <AppointmentCard
                    patients={rightAppointments}
                    title="Appointments 11-15"
                    column="right"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="card border-0">
              <div className="card-body text-center py-5">
                <p className="text-muted" style={{ fontSize: "16px" }}>
                  No appointments found
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Reschedule Modal */}
      <Modal
        show={showRescheduleModal}
        onHide={() => !rescheduleModalLoading && setShowRescheduleModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton={!rescheduleModalLoading}>
          <Modal.Title>Reschedule Appointment</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">Select Date </label>
            <input
              type="date"
              className="form-control"
              value={modalDate}
              min={getTodayDateString()}
              onChange={(e) => setModalDate(e.target.value)}
              disabled={rescheduleModalLoading}
              style={{ height: "45px" }}
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold"> Branch </label>
            <Select
              value={modalBranch}
              onChange={setModalBranch}
              options={branches.map((b) => ({
                value: b.branch_id,
                label: b.branch_name,
              }))}
              placeholder="Select Branch"
              isClearable
              isDisabled={rescheduleModalLoading}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "45px",
                }),
              }}
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold">Select Mode </label>
            <select
              className="form-select"
              value={modalMode}
              onChange={(e) => setModalMode(e.target.value)}
              disabled={rescheduleModalLoading}
              style={{ height: "45px" }}
            >
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowRescheduleModal(false)}
            disabled={rescheduleModalLoading}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleRescheduleConfirm}
            disabled={!modalDate || !modalBranch || rescheduleModalLoading}
          >
            {rescheduleModalLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Rescheduling...
              </>
            ) : (
              "Confirm Reschedule"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Appointmentlist;